<template>
  <div class="card">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="mb-0">
          <i class="fa fa-search me-1"></i>
          Recherche produit
        </h6>

        <span v-if="isLoading" class="small text-muted d-flex align-items-center">
          <i class="fa fa-spinner fa-spin me-1"></i>
          Chargement produits...
        </span>
      </div>

      <!-- Search -->
      <div class="mb-2 position-relative">
        <input
          type="text"
          class="form-control"
          v-model="search"
          placeholder="Rechercher par nom ou code..."
        />
        <!-- Croix pour vider -->
        <button
          v-if="search"
          type="button"
          class="btn btn-sm btn-link text-muted picker-search-clear"
          @click="clearSearch"
          title="Vider"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>

      <!-- Table -->
      <PrimevueDataTable
        :value="filteredProducts"
        :loading="isLoading"
        class="p-datatable-sm"
        responsiveLayout="scroll"
        dataKey="id"
        v-model:first="first"
        v-model:rows="rows"
        :paginator="true"
        :rowsPerPageOptions="[10, 25, 50, 100]"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="Page {currentPage} / {totalPages}"
        v-if="filteredProducts.length"
      >
        <PrimevueColumn header="Produit">
          <template #body="slotProps">
            <div class="d-flex flex-column">
              <span class="fw-semibold">{{ slotProps.data.name }}</span>
              <small class="text-muted">
                {{ slotProps.data.sku || '-' }}
                <span v-if="slotProps.data.barcode"> · {{ slotProps.data.barcode }}</span>
              </small>
            </div>
          </template>
        </PrimevueColumn>

        <PrimevueColumn header="Stock" style="width: 110px;">
          <template #body="slotProps">
            <div class="text-end">
              {{ slotProps.data.stock_quantity != null ? slotProps.data.stock_quantity : '-' }}
            </div>
          </template>
        </PrimevueColumn>

        <PrimevueColumn header="PU" style="width: 120px;">
          <template #body="slotProps">
            <div class="text-end">
              <div v-if="slotProps.data.has_active_promotion" class="fw-semibold text-success">
                {{ $format.currency(slotProps.data.effective_sale_price || 0) }}
              </div>
              <div :class="slotProps.data.has_active_promotion ? 'text-muted text-decoration-line-through small' : ''">
                {{ $format.currency(slotProps.data.sale_price || 0) }}
              </div>
            </div>
          </template>
        </PrimevueColumn>

        <PrimevueColumn header="Qté" style="width: 90px">
  <template #body="slotProps">
    <div v-if="isAlreadyAdded(slotProps.data)" role="status">
      <span class="badge text-bg-success">Déjà ajouté</span>
      <small class="text-muted d-block">Modifiez la quantité dans le tableau.</small>
    </div>
    <input
      v-else
      type="number"
      min="1"
      class="form-control form-control-sm text-end"
      style="width: 70px"
      v-model.number="quantities[slotProps.data.id]"
    />
  </template>
</PrimevueColumn>


        <PrimevueColumn header="" style="width: 70px;">
          <template #body="slotProps">
            <div class="text-end">
              <button
                v-if="!isAlreadyAdded(slotProps.data)"
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click="add(slotProps.data)"
                :disabled="disabled"
                title="Ajouter"
              >
                <i class="fa fa-plus"></i>
              </button>
            </div>
          </template>
        </PrimevueColumn>
      </PrimevueDataTable>

      <div v-else class="text-muted small">
        <span v-if="!isLoading">
          Aucun produit trouvé. Tapez un nom ou une référence.
        </span>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'SaleProductsPicker',
  props: {
    disabled: { type: Boolean, default: false },
    selectedProductIds: { type: Array, default: null },
  },
  data() {
    return {
      isLoading: false,
      products: [],
      search: '',
      quantities: {},
      pendingAddedIds: [],

      // pagination DataTable (frontend)
      first: 0,
      rows: 10,
    };
  },
  computed: {
    filteredProducts() {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) return this.products;

      return (this.products || []).filter((p) => {
        const name = (p.name || '').toLowerCase();
        const sku = (p.sku || '').toLowerCase();
        const barcode = (p.barcode || '').toLowerCase();
        return name.includes(q) || sku.includes(q) || barcode.includes(q);
      });
    },
  },
  watch: {
    search() {
      // reset pagination when searching
      this.first = 0;
    },
  },
  mounted() {
    this.fetchProducts();
  },
  methods: {
    isAlreadyAdded(product) {
      if (!Array.isArray(this.selectedProductIds)) return false;
      return [...this.selectedProductIds, ...this.pendingAddedIds].some((id) => Number(id) === Number(product.id));
    },
    clearSearch() {
      this.search = '';
    },

    async fetchProducts() {
      this.isLoading = true;
      try {
        const res = await this.$api.get('/products');
        const data = res.data.data || res.data || [];

        this.products = data.map((p) => ({
          id: p.id,
          name: p.name,
          sku: p.sku,
          barcode: p.barcode,
          sale_price: p.sale_price,
          regular_sale_price: p.regular_sale_price ?? p.sale_price,
          effective_sale_price: p.effective_sale_price ?? p.sale_price,
          has_active_promotion: Boolean(p.has_active_promotion),
          active_promotion: p.active_promotion || null,
          stock_quantity: p.stock_quantity,
        }));

        // init qty = 1
        const q = {};
        this.products.forEach((p) => {
          q[p.id] = 1;
        });
        this.quantities = q;
      } catch (e) {
        console.error('SaleProductsPicker fetchProducts error', e);
        this.products = [];
      } finally {
        this.isLoading = false;
      }
    },
    add(product) {
  if (this.disabled || !product || this.isAlreadyAdded(product)) return;

  if (Array.isArray(this.selectedProductIds)) {
    this.pendingAddedIds.push(product.id);
    this.$nextTick(() => { this.pendingAddedIds = this.pendingAddedIds.filter((id) => id !== product.id); });
  }

  const qtyRaw = this.quantities[product.id];
  const qty = typeof qtyRaw === 'number' && qtyRaw > 0 ? qtyRaw : 1;

  this.$emit('add', { product, quantity: qty });

  // reset qty to 1 after add (Vue 3 => pas de this.$set)
  this.quantities = {
    ...this.quantities,
    [product.id]: 1,
  };
},



  },
};
</script>

<style scoped>
/* Croix pour vider la recherche */
.picker-search-clear {
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
}
</style>
